import React, {
  ReactElement, Ref, useCallback, useEffect, useRef, useState,
} from 'react';
import cs from 'classnames';
import { animated, useSpring, useSprings } from 'react-spring';
import video from '../../../video/trailer.webm';

import style from './style.module.scss';
import { FilmCard } from '../../filmCard';
import { FilmTrack } from '../../filmTrack';

import image1 from '../../../images/track/1.webp';
import image2 from '../../../images/track/2.webp';
import image3 from '../../../images/track/3.webp';
import image4 from '../../../images/track/4.webp';
import image5 from '../../../images/track/5.webp';
import image6 from '../../../images/track/6.webp';
import image7 from '../../../images/track/7.webp';
import image8 from '../../../images/track/8.webp';
import image9 from '../../../images/track/9.webp';
import image10 from '../../../images/track/10.webp';
import image11 from '../../../images/track/11.webp';
import image12 from '../../../images/track/12.webp';
import image13 from '../../../images/track/13.webp';
import image14 from '../../../images/track/14.webp';
import image15 from '../../../images/track/15.webp';
import image16 from '../../../images/track/16.webp';
import image17 from '../../../images/track/17.webp';
import image18 from '../../../images/track/18.webp';
import image19 from '../../../images/track/19.webp';
import image20 from '../../../images/track/20.webp';
import image21 from '../../../images/track/21.webp';

import { ReactComponent as NetflixLogo } from '../../../images/logo/netflix.svg';
import { ReactComponent as MoreTVLogo } from '../../../images/logo/moretv.svg';
import { ReactComponent as YTLogo } from '../../../images/logo/yt.svg';
import { ReactComponent as DisneyLogo } from '../../../images/logo/disney.svg';
import { ReactComponent as KinopoiskLogo } from '../../../images/logo/kinopoisk.svg';
import { ReactComponent as WinkLogo } from '../../../images/logo/wink.svg';
import { ReactComponent as PremierLogo } from '../../../images/logo/premier.svg';
import { useIntersectionObserver } from '../../../utils/hooks/useIntersectionObserver';

export const CinemaSection = (): ReactElement => {
  const [showVideo, setVideoVisibility] = useState(true);
  const [currentScroll, setCurrentScroll] = useState(0);

  const videoRef = useRef<HTMLVideoElement>();
  const containerRef = useRef<HTMLVideoElement>();
  const anchorVideo = useRef<HTMLVideoElement>();

  const isVisible = useIntersectionObserver(videoRef);
  const isContainerVisible = useIntersectionObserver(containerRef);

  const finishedTrackState = { scale: 1, opacity: 1, transform: 'perspective(600px) rotateY(0deg)' };

  const condition = isContainerVisible && !showVideo;

  const trackOneItems = [[image6, PremierLogo], [image7, YTLogo], [image8, MoreTVLogo], [image9, WinkLogo], [image10, KinopoiskLogo], [image18, KinopoiskLogo], [image19, NetflixLogo]];
  const trackTwoItems = [[image11, KinopoiskLogo], [image12, DisneyLogo], [image13, KinopoiskLogo], [image14, KinopoiskLogo], [image15, WinkLogo], [image20, KinopoiskLogo], [image21, KinopoiskLogo]];

  const [trackOne, trackOneAPI] = useSprings(5, (index) => ({
    scale: condition ? 1 : 0, opacity: condition ? 1 : 0, transform: condition ? 'perspective(600px) rotateY(0deg)' : 'perspective(600px) rotateY(90deg)', config: { duration: index * 250 },
  }));

  const [trackTwo, trackTwoAPI] = useSprings(5, (index) => ({
    scale: condition ? 1 : 0, opacity: condition ? 1 : 0, transform: condition ? 'perspective(600px) rotateY(0deg)' : 'perspective(600px) rotateY(90deg)', config: { duration: index * 250 },
  }));

  const onScrollMainVideo = useCallback((): void => {
    if (isContainerVisible) {
      setCurrentScroll(window.scrollY + document.documentElement.clientHeight);
    }
  }, [isContainerVisible]);

  useEffect(() => {
    document.addEventListener('scroll', onScrollMainVideo);

    return () => document.removeEventListener('scroll', onScrollMainVideo);
  }, [onScrollMainVideo]);

  // const videoSpring = useSpring({ transition: showVideo ? '3s' : '0s', left: showVideo ?  });

  useEffect(() => {
    if (showVideo && isVisible && videoRef && videoRef.current && anchorVideo.current && videoRef.current?.clientHeight && currentScroll > videoRef.current?.getBoundingClientRect().y + window.scrollY + videoRef.current?.clientHeight / 1.5) {
      /* TODO: Отнимать текущее значение прокрутки */
      videoRef.current!.style.transition = '3s';

      videoRef.current!.style.left = `${anchorVideo?.current.offsetLeft - 200}px`;
      videoRef.current!.style.top = `${anchorVideo?.current.offsetTop}px`;

      videoRef.current!.style.width = '375px';
      videoRef.current!.style.height = '200px';
      videoRef.current!.style.borderRadius = '12px';

      trackOneAPI.start(() => (finishedTrackState));
      trackTwoAPI.start(() => (finishedTrackState));

      videoRef.current?.addEventListener('transitionend', () => {
        setVideoVisibility(false);
      });
    }
  }, [isVisible, currentScroll, showVideo]);

  useEffect(() => {
    if (isContainerVisible && !showVideo) {
      trackOneAPI.start(() => (finishedTrackState));
      trackTwoAPI.start(() => (finishedTrackState));
    } else if (!showVideo && !isContainerVisible) {
      trackOneAPI.start(() => ({ scale: 0, opacity: 0, transform: 'perspective(600px) rotateY(90deg)' }));
      trackTwoAPI.start(() => ({ scale: 0, opacity: 0, transform: 'perspective(600px) rotateY(90deg)' }));
    }
  }, [isContainerVisible, showVideo]);

  return (
    <section className={style.cinemaSection} ref={containerRef as Ref<any>}>
      {showVideo && (
        <video preload="auto" style={{ width: '100vw', height: '100vh', left: 0 }} className={style.card} ref={videoRef as Ref<any>} autoPlay muted loop>
          <source src={video} type="video/webm" />
        </video>
      ) }
      <FilmTrack className={style.filmTrack} classNames={{ container: cs(style.filmContainer, style.filmTrackDuration) }} x={`-${!showVideo ? (currentScroll - 1200) : 200}px`}>
        <FilmCard image={image4} logo={NetflixLogo} />
        <FilmCard image={image5} logo={NetflixLogo} ref={anchorVideo} />
        <FilmCard image={image1} logo={NetflixLogo} />
        <FilmCard image={image2} logo={NetflixLogo} />
        <FilmCard image={image3} logo={NetflixLogo} />
        <FilmCard image={image16} logo={KinopoiskLogo} />
        <FilmCard image={image17} logo={KinopoiskLogo} />
      </FilmTrack>
      <FilmTrack className={style.filmTrack} classNames={{ container: cs(style.filmContainer, style.filmTrackDuration) }} x={`${!showVideo ? (currentScroll - 1700) : -300}px`}>
        {trackOne.map((style, index) => <animated.div style={style}><FilmCard image={trackOneItems[index][0] as string} logo={trackOneItems[index][1] as any} /></animated.div>)}
      </FilmTrack>
      <FilmTrack className={style.filmTrack} classNames={{ container: cs(style.filmContainer, style.filmTrackDuration) }} x={`-${!showVideo ? (currentScroll - 1400) : 150}px`}>
        {trackTwo.map((style, index) => <animated.div style={style}><FilmCard image={trackTwoItems[index][0] as string} logo={trackTwoItems[index][1] as any} /></animated.div>)}
      </FilmTrack>
    </section>
  );
};
